/**
 * Wspólna normalizacja tekstu używana w całej aplikacji do dopasowań:
 * - mapuje ręcznie polskie `ł`/`Ł` (NFD ich nie rozkłada na bazową literę
 *   + diakrytyk, więc bez tego "lomzy" vs "Łomży" by nie zadziałało)
 * - usuwa znaki diakrytyczne przez NFD + filtr `\p{Diacritic}`
 * - sprowadza do lowercase i przycina białe znaki na końcach
 *
 * Funkcja jest idempotentna: `normalizeText(normalizeText(x)) === normalizeText(x)`.
 * To JEDYNE źródło prawdy dla normalizacji adresowo-tekstowej — pozostałe
 * helpery (np. `normalizeForMatch`, `createAsciiSlug`, `matchesStreetQuery`,
 * a także helpery w komponentach share) MUSZĄ z niej korzystać, żeby nie
 * doszło do rozjazdu reguł (np. brak obsługi wielkiego "Ł").
 */
export const normalizeText = (value: string): string => {
  if (!value) return "";
  return value
    // Najpierw mapujemy ł/Ł — NFD ich nie rozłoży, więc musimy ręcznie.
    // Robimy to PRZED NFD/lowercase, żeby obsłużyć obie wielkości liter.
    .replace(/ł/g, "l")
    .replace(/Ł/g, "L")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
};

/**
 * Wariant `normalizeText` przeznaczony do PORÓWNYWANIA nazw (miast, ulic).
 * Dodatkowo usuwa wszystkie białe znaki, dzięki czemu "Zielona Góra",
 * "zielonagora" i "ZIELONA  GORA" dają identyczny klucz porównawczy.
 * Nie używać do wyświetlania ani do dzielenia na słowa.
 */
export const normalizeForMatch = (value: string): string => {
  return normalizeText(value).replace(/\s+/g, "");
};

/**
 * Buduje slug ASCII (a-z, 0-9, myślniki) z dowolnego tekstu, używając
 * wspólnej `normalizeText`. Wykorzystywany m.in. przez modale udostępniania
 * (nazwy plików PNG) — utrzymujemy jedną regułę dla całej aplikacji.
 */
export const createAsciiSlug = (value: string): string => {
  return normalizeText(value)
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

// Map of ASCII characters to Polish diacritics for common Polish letters
const polishDiacriticsMap: Record<string, string> = {
  'a': 'ą',
  'c': 'ć',
  'e': 'ę',
  'l': 'ł',
  'n': 'ń',
  'o': 'ó',
  's': 'ś',
  'z': 'ż', // Also maps to ź but ż is more common
};

// Generate all possible Polish diacritic variants of a word
// e.g., "sloneczna" -> ["sloneczna", "słoneczna", "slonęczna", ...]
// Returns the most likely Polish variant based on common patterns
export const addPolishDiacritics = (text: string): string => {
  if (!text) return text;
  
  // Common Polish word patterns - map normalized form to proper Polish
  const commonPatterns: Record<string, string> = {
    'sloneczna': 'Słoneczna',
    'sloneczne': 'Słoneczne', 
    'zolnierska': 'Żołnierska',
    'zolnierzy': 'Żołnierzy',
    'zrodlana': 'Źródlana',
    'zrodlowa': 'Źródłowa',
    'swidnicka': 'Świdnicka',
    'sniezna': 'Śnieżna',
    'gorczanska': 'Gorczańska',
    'laczna': 'Łączna',
    'lakowa': 'Łąkowa',
    'lodowa': 'Lodowa',
    'cicha': 'Cicha',
    'dluga': 'Długa',
    'gorna': 'Górna',
    'dolna': 'Dolna',
    'glowna': 'Główna',
    'polna': 'Polna',
    'lesna': 'Leśna',
    'kwiatowa': 'Kwiatowa',
    'ogrodowa': 'Ogrodowa',
    'parkowa': 'Parkowa',
    'sportowa': 'Sportowa',
    'szkolna': 'Szkolna',
    'koscielna': 'Kościelna',
    'kolejowa': 'Kolejowa',
    'dworcowa': 'Dworcowa',
    'targowa': 'Targowa',
    'rynek': 'Rynek',
    'wolnosci': 'Wolności',
    'niepodleglosci': 'Niepodległości',
    'konstytucji': 'Konstytucji',
    'powstancow': 'Powstańców',
    'zielona': 'Zielona',
    'biala': 'Biała',
    'czerwona': 'Czerwona',
    'rozana': 'Różana',
    'brzozowa': 'Brzozowa',
    'debowa': 'Dębowa',
    'sosnowa': 'Sosnowa',
    'lipowa': 'Lipowa',
    'klonowa': 'Klonowa',
    'topolowa': 'Topolowa',
    'jabloniowa': 'Jabłoniowa',
    'wisniowa': 'Wiśniowa',
    'kasztanowa': 'Kasztanowa',
    'akacjowa': 'Akacjowa',
    'jasminowa': 'Jaśminowa',
    'rozowa': 'Różowa',
    'modrzewiowa': 'Modrzewiowa',
    'swierczewskiego': 'Świerczewskiego',
    'mickiewicza': 'Mickiewicza',
    'slowackiego': 'Słowackiego',
    'sienkiewicza': 'Sienkiewicza',
    'zeromskiego': 'Żeromskiego',
    'orzeszkowej': 'Orzeszkowej',
    'konopnickiej': 'Konopnickiej',
    'kopernika': 'Kopernika',
    'pilsudskiego': 'Piłsudskiego',
    'kosciuszki': 'Kościuszki',
    'poniatowskiego': 'Poniatowskiego',
    'dabrowskiego': 'Dąbrowskiego',
    'krolowej': 'Królowej',
    'jadwigi': 'Jadwigi',
    'jana': 'Jana',
    'pawla': 'Pawła',
    'swietego': 'Świętego',
    'sw': 'Św',
  };

  const normalized = normalizeText(text);
  
  // Check if we have a known pattern
  if (commonPatterns[normalized]) {
    return commonPatterns[normalized];
  }
  
  // For unknown words, try basic letter substitutions for the most common patterns
  let result = text;
  
  // Apply common substitutions if the text looks like it needs Polish characters
  // This is a heuristic approach for words not in our dictionary
  const substitutions: [RegExp, string][] = [
    [/\bsl(?=[aeiou])/gi, 'Sł'],  // sl + vowel -> Sł (like Słoneczna)
    [/\bz(?=[aeiou])/gi, 'Ż'],   // z + vowel at start -> Ż (like Żołnierska)
    [/\bs(?=[wn])/gi, 'Ś'],      // s before w or n -> Ś (like Świdnicka, Śnieżna)
    [/n(?=sk)/gi, 'ń'],          // n before sk -> ń (like Gorczańska)
    [/l(?=sk)/gi, 'ł'],          // l before sk -> ł (like Krakowska stays but Łącka)
  ];
  
  // Only apply if text is all ASCII (no Polish chars already)
  if (!/[ąćęłńóśźżĄĆĘŁŃÓŚŹŻ]/.test(text)) {
    // Don't modify, just return original - let Nominatim handle it
    // The issue is Nominatim needs Polish chars, so we search with both variants
  }
  
  return text;
};

// Create search variants with and without Polish diacritics
export const getSearchVariants = (text: string): string[] => {
  if (!text) return [text];
  
  const normalized = normalizeText(text);
  const variants = new Set<string>([text]);
  
  // Common Polish word patterns - map normalized form to proper Polish
  const commonPatterns: Record<string, string> = {
    'sloneczna': 'Słoneczna',
    'sloneczne': 'Słoneczne',
    'sloneczny': 'Słoneczny',
    'zolnierska': 'Żołnierska',
    'swidnicka': 'Świdnicka',
    'sniezna': 'Śnieżna',
    'zrodlana': 'Źródlana',
    'zrodlowa': 'Źródłowa',
    'laczna': 'Łączna',
    'lakowa': 'Łąkowa',
    'dluga': 'Długa',
    'gorna': 'Górna',
    'dolna': 'Dolna',
    'glowna': 'Główna',
    'lesna': 'Leśna',
    'koscielna': 'Kościelna',
    'wolnosci': 'Wolności',
    'niepodleglosci': 'Niepodległości',
    'powstancow': 'Powstańców',
    'zielona': 'Zielona',
    'biala': 'Biała',
    'rozana': 'Różana',
    'debowa': 'Dębowa',
    'jabloniowa': 'Jabłoniowa',
    'wisniowa': 'Wiśniowa',
    'jasminowa': 'Jaśminowa',
    'rozowa': 'Różowa',
    'swierczewskiego': 'Świerczewskiego',
    'slowackiego': 'Słowackiego',
    'zeromskiego': 'Żeromskiego',
    'pilsudskiego': 'Piłsudskiego',
    'kosciuszki': 'Kościuszki',
    'dabrowskiego': 'Dąbrowskiego',
    'krolowej': 'Królowej',
    'pawla': 'Pawła',
    'swietego': 'Świętego',
    'lomza': 'Łomża',
    'lomzy': 'Łomży',
    'lodz': 'Łódź',
    'lodzi': 'Łodzi',
    'gdansk': 'Gdańsk',
    'gdanska': 'Gdańska',
    'poznan': 'Poznań',
    'poznania': 'Poznania',
    'wroclaw': 'Wrocław',
    'wroclawia': 'Wrocławia',
    'krakow': 'Kraków',
    'krakowa': 'Krakowa',
    'bialystok': 'Białystok',
    'bialystoku': 'Białegostoku',
    'czestochowa': 'Częstochowa',
    'czestochowy': 'Częstochowy',
    'swinoujscie': 'Świnoujście',
    'kolobrzeg': 'Kołobrzeg',
    'miedzyzdroje': 'Międzyzdroje',
  };

  // Check if we have a known pattern for whole word or partial match
  for (const [key, value] of Object.entries(commonPatterns)) {
    if (normalized === key || normalized.includes(key)) {
      variants.add(value);
      variants.add(text.replace(new RegExp(key, 'gi'), value));
    }
  }
  
  return Array.from(variants);
};

export const includesNormalized = (haystack: string, needle: string): boolean => {
  const h = normalizeText(haystack);
  const n = normalizeText(needle);
  if (!n) return false;
  return h.includes(n);
};

export const startsWithNormalized = (value: string, prefix: string): boolean => {
  const v = normalizeText(value);
  const p = normalizeText(prefix);
  if (!p) return false;
  if (v.startsWith(p)) return true;
  // Spójność: "zielonag" jako prefiks też powinno pasować do "zielona góra"
  return normalizeForMatch(value).startsWith(normalizeForMatch(prefix));
};

/**
 * Dopasowanie do POCZĄTKU dowolnego słowa w wartości (a nie do dowolnego
 * miejsca w środku). Eliminuje fałszywki typu "warz" → "stoWARZyszenie".
 * Słowa rozdzielamy spacją, myślnikiem, kropką i nawiasem.
 */
export const matchesWordPrefix = (value: string, query: string): boolean => {
  const v = normalizeText(value);
  const q = normalizeText(query);
  if (!q) return false;
  if (v.startsWith(q)) return true;
  // \b nie obsłuży dobrze polskich liter po normalizacji, więc rozbijamy ręcznie.
  const words = v.split(/[\s\-./()_,"'„""«»‚'`]+/).filter(Boolean);
  return words.some((w) => w.startsWith(q));
};

/**
 * Dopasowanie schroniska do zapytania użytkownika.
 * - 1 słowo: zachowanie jak dotąd (prefiks słowa w nazwie LUB substring w mieście/regionie).
 * - >=2 słowa (wklejone pełne nazwy typu „Schronisko dla Bezdomnych Zwierząt w Celestynowie"):
 *   każdy token (>=2 znaki) musi pasować do nazwy LUB miasta LUB regionu, ale niekoniecznie
 *   wszystkie do tego samego pola - dzięki temu długi tytuł znajduje krótszą realną nazwę
 *   w odpowiednim mieście. Krótkie tokeny (1 znak, spójniki) są ignorowane.
 */
export const matchesShelterQuery = (
  name: string,
  city: string,
  region: string | null | undefined,
  query: string,
): boolean => {
  const q = query.trim();
  if (!q) return true;
  const STOPWORDS = new Set(["dla","i","w","we","z","ze","na","do","od","po","za","u","o","a","oraz","przy"]);
  // Dzielimy zapytanie na te same separatory, którymi rozbijamy słowa w polach.
  // Dzięki temu wklejki w cudzysłowach, z myślnikami, nawiasami i interpunkcją
  // nie sklejają się w jeden "token" i nie psują dopasowania.
  const rawTokens = q.split(/[\s\-./()_,;:!?"'„""«»‚'`\[\]]+/)
    .filter((t) => t.length >= 2 && !STOPWORDS.has(normalizeText(t)));
  if (rawTokens.length <= 1) {
    const matchesName = matchesWordPrefix(name, q);
    const matchesCity = includesNormalized(city, q);
    const matchesRegion = region ? includesNormalized(region, q) : false;
    return matchesName || matchesCity || matchesRegion;
  }
  // Pomocnik: dopasowanie tokenu do pola tekstowego z tolerancją na polskie odmiany
  // (np. „Kutnie" vs „Kutno", „Celestynowie" vs „Celestynów", „łódzkim" vs „łódzkie").
  // Akceptujemy gdy: substring w którąś stronę LUB wspólny prefiks >=4 znaki na granicy słowa.
  const tokenMatchesField = (field: string, tok: string): boolean => {
    const f = normalizeText(field);
    const t = normalizeText(tok);
    if (!t || !f) return false;
    if (f.includes(t) || t.includes(f)) return true;
    const words = f.split(/[\s\-./()_,"'„""«»‚'`]+/).filter(Boolean);
    return words.some((w) => {
      const n = Math.min(w.length, t.length);
      if (n < 4) return false;
      let i = 0;
      while (i < n && w.charCodeAt(i) === t.charCodeAt(i)) i++;
      return i >= 4;
    });
  };
  // Heurystyka dla długich wklejek: nie wymagamy 100% dopasowania tokenów (DB ma często
  // krótszą nazwę niż wklejony pełny tytuł), ale wymagamy:
  //   1) co najmniej połowy tokenów dopasowanej do nazwy/miasta/regionu (ceil),
  //   2) geograficznej kotwicy - przynajmniej jeden token musi pasować do miasta LUB regionu.
  let matchedCount = 0;
  let nameMatchedCount = 0;
  let anchored = false;
  for (const tok of rawTokens) {
    const inCity = tokenMatchesField(city, tok);
    const inRegion = region ? tokenMatchesField(region, tok) : false;
    const inName = matchesWordPrefix(name, tok);
    if (inCity || inRegion) anchored = true;
    if (inName) nameMatchedCount++;
    if (inName || inCity || inRegion) matchedCount++;
  }
  // Jeśli WSZYSTKIE tokeny pasują do samej nazwy (użytkownik wkleił pełną nazwę
  // schroniska), akceptujemy bez wymogu kotwicy geograficznej.
  if (nameMatchedCount === rawTokens.length) return true;
  const required = Math.ceil(rawTokens.length / 2);
  return anchored && matchedCount >= required;

};

/**
 * Bounded Levenshtein – zwraca true, jeśli odległość edycyjna między `a` i `b`
 * NIE PRZEKRACZA `max`. Implementacja iteracyjna O(len(a) * len(b)) z wczesnym
 * przerwaniem (każdy wiersz musi mieć choć jedną wartość ≤ max).
 */
const editDistanceWithin = (a: string, b: string, max: number): boolean => {
  if (a === b) return true;
  const la = a.length;
  const lb = b.length;
  if (Math.abs(la - lb) > max) return false;
  if (la === 0) return lb <= max;
  if (lb === 0) return la <= max;

  let prev = new Array(lb + 1);
  let curr = new Array(lb + 1);
  for (let j = 0; j <= lb; j++) prev[j] = j;

  for (let i = 1; i <= la; i++) {
    curr[0] = i;
    let rowMin = curr[0];
    for (let j = 1; j <= lb; j++) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      curr[j] = Math.min(
        prev[j] + 1,        // deletion
        curr[j - 1] + 1,    // insertion
        prev[j - 1] + cost, // substitution
      );
      if (curr[j] < rowMin) rowMin = curr[j];
    }
    if (rowMin > max) return false;
    [prev, curr] = [curr, prev];
  }
  return prev[lb] <= max;
};

/**
 * Grupy równoważnych form skrótów ulicowych po normalizacji (bez kropek,
 * bez diakrytyków, lowercase). Każda grupa łączy skrót z jego pełnymi
 * formami fleksyjnymi. Wykorzystywane przez `matchesStreetQuery`, żeby:
 *  - „ks. Ledóchowskiej" pasowało do zapytania „ksiedza ledochowsk",
 *  - „Aleja Wojska" pasowało do zapytania „al wojska",
 *  - „ks." w zapytaniu nie blokowało dopasowania nazwy bez tego prefiksu.
 *
 * Dodawanie nowych grup: zachowaj tę samą konwencję (znormalizowane formy).
 */
/**
 * Definicja jednej grupy skrótów ulicowych.
 * - `forms`: znormalizowane formy (lowercase, bez diakrytyków, bez kropek).
 *   Pierwsza forma to kanoniczny skrót.
 * - `prefix`: jeśli `true`, grupa jest typowym prefiksem nazwy ulicy
 *   („ul.", „al.", „pl.", „os.") — używana przez `stripRoadPrefix` i
 *   `STREET_PREFIX_RE` w `addressInput.ts`.
 */
interface StreetAbbrevGroup {
  forms: readonly string[];
  prefix?: boolean;
}

export const STREET_ABBREV_GROUPS: ReadonlyArray<StreetAbbrevGroup> = [
  // Prefiksy typu ulicowego — rozpoznawane także przez stripRoadPrefix
  // i regex normalizujący input adresowy.
  {
    forms: ["ul", "ulica", "ulicy", "ulice", "ulicami", "ulicach"],
    prefix: true,
  },
  {
    forms: ["al", "aleja", "aleje", "alei", "aleji", "alejami", "alejach"],
    prefix: true,
  },
  {
    forms: ["pl", "plac", "placu", "place", "placow", "placami", "placach"],
    prefix: true,
  },
  {
    forms: [
      "os",
      "osiedle",
      "osiedla",
      "osiedlu",
      "osiedli",
      "osiedlami",
      "osiedlach",
    ],
    prefix: true,
  },
  {
    forms: ["rondo", "ron", "ronda", "rondzie", "rond", "rondami", "rondach"],
    prefix: true,
  },
  // Tytuły / godności — opcjonalne tokeny w środku zapytania
  { forms: ["ks", "ksiedza", "ksiezy", "ksiedz"] },
  { forms: ["sw", "swiety", "swieta", "swietego", "swietej", "swietych"] },
  { forms: ["gen", "generala", "general"] },
  { forms: ["plk", "pulkownika", "pulkownik"] },
  { forms: ["mjr", "majora", "major"] },
  { forms: ["por", "porucznika", "porucznik"] },
  { forms: ["ppor", "podporucznika"] },
  { forms: ["bp", "biskupa", "biskup"] },
  { forms: ["dr", "doktora", "doktor"] },
  { forms: ["prof", "profesora", "profesor"] },
  { forms: ["bl", "blogoslawionego", "blogoslawionej", "blogoslawiony"] },
];

const ABBREV_GROUP_INDEX = new Map<string, number>();
STREET_ABBREV_GROUPS.forEach((group, idx) =>
  group.forms.forEach((form) => {
    if (ABBREV_GROUP_INDEX.has(form)) {
      throw new Error(
        `STREET_ABBREV_GROUPS: zduplikowana forma "${form}" (grupa ${idx} oraz ${ABBREV_GROUP_INDEX.get(form)})`,
      );
    }
    ABBREV_GROUP_INDEX.set(form, idx);
  }),
);

const isAbbrevMarker = (s: string): boolean => ABBREV_GROUP_INDEX.has(s);

const sameAbbrevGroup = (a: string, b: string): boolean => {
  const ga = ABBREV_GROUP_INDEX.get(a);
  if (ga === undefined) return false;
  return ABBREV_GROUP_INDEX.get(b) === ga;
};

/** Strict (bez fuzzy) test: czy `token` równoważny prefiksowi słowa `word`. */
const tokenMatchesWordStrict = (token: string, word: string): boolean => {
  if (word.startsWith(token)) return true;
  // Pełna równoważność w obrębie grupy skrótów (np. "ksiedza" ↔ "ks").
  if (sameAbbrevGroup(token, word)) return true;
  return false;
};

export interface MatchesStreetQueryOptions {
  /**
   * Tryb tolerancyjny dla literówek. Domyślnie wyłączony.
   * - tokeny o długości < 4 nadal wymagają ścisłego prefiksu (zbyt krótkie,
   *   żeby fuzzy nie generowało fałszywek typu "kop" → "kos…").
   * - 4–6 znaków: dozwolona 1 edycja (literówka, brak/dodatkowy znak).
   * - ≥ 7 znaków: dozwolone 2 edycje.
   */
  fuzzy?: boolean;
}

/**
 * Dopasowanie zapytania ulicowego do nazwy ulicy.
 * - normalizuje diakrytyki ("ledóchowskiej" ≈ "ledochowskiej") + "ł" → "l"
 * - tokenizuje zapytanie po spacjach/myślnikach/kropkach (np. "ks. ledochowsk")
 * - KAŻDY token (≥2 znaki) musi pasować do nazwy ulicy jako prefiks słowa
 *   ALBO jako równoważna forma znanego skrótu (np. "ks" ↔ "księdza",
 *   "al" ↔ "aleja", "św" ↔ "świętego").
 * - Tokeny będące znanymi skrótami (ks, św, al, ul, pl, …) są OPCJONALNE
 *   — jeżeli nie pasują do żadnego słowa, są pomijane (np. zapytanie
 *   „ks. ledochowsk" pasuje do nazwy „Ledóchowskiej" bez prefiksu „ks.").
 * - Co najmniej JEDEN token musi mieć twarde dopasowanie — samo „ks" lub
 *   „al" nie zwraca true dla dowolnej nazwy.
 * - Tokeny krótsze niż 2 znaki są pomijane jako szum (np. „w", „o").
 * - z `{ fuzzy: true }` dopuszcza drobne literówki (patrz opcje).
 */
export const matchesStreetQuery = (
  roadName: string,
  query: string,
  options: MatchesStreetQueryOptions = {},
): boolean => {
  const v = normalizeText(roadName);
  if (!v) return false;
  const q = normalizeText(query);
  if (!q) return false;

  const SPLIT = /[\s\-./()_,]+/;
  const words = v.split(SPLIT).filter(Boolean);
  const tokens = q.split(SPLIT).filter((t) => t.length >= 2);
  if (tokens.length === 0) return false;

  // Szybka ścieżka: pełny prefiks całego zapytania, ale TYLKO jeśli zapytanie
  // ma choć jeden token „realny" (nie sam skrót). Inaczej "ks" jako zapytanie
  // pasowałoby do "ks. Ledóchowskiej" przez startsWith.
  if (v.startsWith(q) && tokens.some((t) => !isAbbrevMarker(t))) return true;

  let hardMatches = 0;

  for (const t of tokens) {
    const tokenIsAbbrev = isAbbrevMarker(t);
    // 1) Strict / equivalence
    if (words.some((w) => tokenMatchesWordStrict(t, w))) {
      // Skróty (ks, al, sw, ul, …) NIGDY nie liczą się jako twarde dopasowanie,
      // nawet jeśli przypadkiem pasują do prefiksu słowa (np. "ks" ↔ "ks.").
      // W przeciwnym razie samo zapytanie "ks" zwracałoby każdą nazwę zaczynającą się od "ks".
      if (!tokenIsAbbrev) hardMatches++;
      continue;
    }
    // 2) Skrót bez twardego dopasowania → opcjonalny, pomijamy (nie liczy się też do fuzzy).
    if (tokenIsAbbrev) continue;
    // 3) Fuzzy (tylko jeśli włączone i token ≥ 4 znaki, i nie jest skrótem)
    if (options.fuzzy && t.length >= 4) {
      const maxEdits = t.length >= 7 ? 2 : 1;
      const fuzzyOk = words.some((w) => {
        if (w.length + maxEdits < t.length) return false;
        const sliceLen = Math.min(w.length, t.length + maxEdits);
        return editDistanceWithin(t, w.slice(0, sliceLen), maxEdits);
      });
      if (fuzzyOk) {
        hardMatches++;
        continue;
      }
    }
    // 4) Token „realny" bez dopasowania → cała nazwa odpada.
    return false;
  }

  // Wymagamy co najmniej jednego twardego dopasowania, żeby same skróty
  // (np. „ks" / „al") nie zwracały dowolnej ulicy.
  return hardMatches > 0;
};

/**
 * Wszystkie formy z grup oznaczonych `prefix: true` — używane do budowy
 * regexów rozpoznających prefiksy ulicowe ("ul.", "ulica", "al.", "plac" itp.).
 * Eksportujemy, żeby `addressInput.ts` i inne helpery korzystały z jednego
 * źródła prawdy.
 */
export const STREET_PREFIX_FORMS: readonly string[] = STREET_ABBREV_GROUPS
  .filter((g) => g.prefix)
  .flatMap((g) => g.forms);

/**
 * Regex dopasowujący prefiks ulicy na początku stringa. Toleruje:
 * - wielokrotne spacje przed/po prefiksie i wokół kropki ("ul.   Polna", "ul . Polna"),
 * - brak spacji po kropce ("ul.Polna"),
 * - brak kropki ("ul Polna", "ulica Polna"),
 * - dowolną wielkość liter.
 * Wymaga, żeby po prefiksie pojawił się jakiś niepusty znak (nazwa ulicy)
 * oraz granicy słowa po samej formie — dzięki temu „ulami Coś" nie zostanie
 * potraktowane jako „ul" + reszta.
 */
export const STREET_PREFIX_RE = new RegExp(
  `^\\s*(?:${STREET_PREFIX_FORMS.join("|")})(?![\\p{L}\\p{N}])\\s*\\.?\\s*(?=[\\p{L}\\p{N}])`,
  "iu",
);

export const stripRoadPrefix = (road: string): string => {
  // Nominatim potrafi zwracać prefiksy typu: "ulica", "ul.", "aleja" itd.
  // Wszystkie warianty pochodzą z STREET_ABBREV_GROUPS (prefix: true).
  return (road || "").trim().replace(STREET_PREFIX_RE, "").trim();
};

export const extractRoadFromDisplayName = (displayName: string): string => {
  const firstPart = (displayName || "").split(",")[0]?.trim() ?? "";
  return stripRoadPrefix(firstPart);
};

// Mapa nazw województw z Nominatim na polskie nazwy z małej litery
export const normalizeRegion = (state: string | undefined): string => {
  if (!state) return "";

  // Nominatim zwraca np. "województwo mazowieckie" - wyciągamy nazwę
  const match = state.match(/województwo\s+(.+)/i);
  if (match) {
    return match[1].toLowerCase();
  }

  // Jeśli format jest inny, zwróć małymi literami
  return state.toLowerCase().replace("województwo", "").trim();
};

// Formatowanie nazwy miasta/adresu z wielkich liter
export const capitalizeWords = (str: string): string => {
  const parts = (str || "").split(" ");
  return parts
    .map((word, idx) => {
      if (word.length === 0) return word;
      // Małe słowa (spójniki itp.) zostawiamy małe, chyba że są na początku
      const smallWords = ["i", "w", "na", "do", "z", "od", "po", "przy", "za", "dla"];
      if (idx > 0 && smallWords.includes(word.toLowerCase())) {
        return word.toLowerCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
};
